import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ChevronsLeftRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const imgSrc = (url) =>
  url && url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const clamp = (v) => Math.min(96, Math.max(4, v));

const CompareSlider = ({ item, index }) => {
  const [pos, setPos] = useState(50);
  const frameRef = useRef(null);
  const dragging = useRef(false);
  const title = item.title || "projet à Genève";

  const setFromClientX = (clientX) => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos(clamp(((clientX - rect.left) / rect.width) * 100));
  };

  const onPointerDown = (e) => {
    dragging.current = true;
    frameRef.current?.setPointerCapture(e.pointerId);
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging.current) setFromClientX(e.clientX);
  };
  const stopDragging = () => {
    dragging.current = false;
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setPos((p) => clamp(p - 5));
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      setPos((p) => clamp(p + 5));
    }
  };

  return (
    <motion.figure
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65, delay: (index % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`gallery-pair-${index + 1}`}
    >
      <div
        ref={frameRef}
        role="slider"
        tabIndex={0}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        aria-label={`Comparer avant et après — ${title}`}
        data-testid={`gallery-compare-${index + 1}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={stopDragging}
        onPointerCancel={stopDragging}
        onKeyDown={onKeyDown}
        className="group relative aspect-[4/3] cursor-ew-resize touch-none select-none overflow-hidden rounded-sm border border-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
      >
        <img
          src={imgSrc(item.after_url)}
          alt={`Après la rénovation — ${title}`}
          data-testid={`gallery-after-${index + 1}`}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          draggable={false}
        />
        <img
          src={imgSrc(item.before_url)}
          alt={`Avant la rénovation — ${title}`}
          data-testid={`gallery-before-${index + 1}`}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
          loading="lazy"
          draggable={false}
        />

        <span
          className={`absolute left-3 top-3 rounded-sm bg-white/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink backdrop-blur-sm transition-opacity duration-200 ${
            pos < 14 ? "opacity-0" : "opacity-100"
          }`}
        >
          Avant
        </span>
        <span
          className={`absolute right-3 top-3 rounded-sm bg-sun px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink transition-opacity duration-200 ${
            pos > 86 ? "opacity-0" : "opacity-100"
          }`}
        >
          Après
        </span>

        <div
          aria-hidden="true"
          className="absolute inset-y-0"
          style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
        >
          <div className="h-full w-px bg-white/90 shadow-[0_0_12px_rgba(15,23,42,0.35)]" />
          <div className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-brand shadow-[0_8px_24px_rgba(15,23,42,0.25)] transition-transform duration-200 group-active:scale-95">
            <ChevronsLeftRight className="h-5 w-5" strokeWidth={2} />
          </div>
        </div>
      </div>
      {item.title && (
        <figcaption className="mt-4 flex items-center gap-3 text-sm font-medium text-slate-600">
          <span className="h-px w-6 bg-brand/50" aria-hidden="true" />
          {item.title}
        </figcaption>
      )}
    </motion.figure>
  );
};

export const AvantApres = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/gallery`)
      .then((res) => {
        if (mounted) setItems(res.data.items || []);
      })
      .catch(() => {
        if (mounted) setItems([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <section id="realisations" data-testid="avant-apres-section" className="bg-white">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14 max-w-2xl"
        >
          <p className="mb-5 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.28em] text-brand">
            <span className="font-display text-sm tracking-normal text-slate-400">04</span>
            <span className="h-px w-8 bg-sun" aria-hidden="true" />
            Nos transformations
          </p>
          <h2
            data-testid="avant-apres-title"
            className="font-display text-3xl font-normal tracking-tight text-ink sm:text-4xl"
          >
            Avant / Après
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
            Faites glisser le curseur sur chaque photo pour découvrir la transformation, avant et
            après notre intervention.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
          {items.map((item, i) => (
            <CompareSlider key={item.id} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
